import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const GIFT_WRAP_PROPERTY = '_gift_wrap';

/**
 * A custom element that adds/swaps a gift-wrap add-on product in the cart via
 * the Cart AJAX API (so inventory, tax, and revenue reporting stay correct),
 * and independently keeps an order-level gift message cart attribute in
 * sync. The message is a complementary service: it's available regardless of
 * whether gift wrap is selected.
 *
 * @typedef {Object} CartGiftWrapComponentRefs
 * @property {HTMLTextAreaElement} message - The gift message textarea.
 * @property {HTMLElement} error - The error message container.
 */

/**
 * @extends {Component<CartGiftWrapComponentRefs>}
 */
class CartGiftWrap extends Component {
  requiredRefs = ['message', 'error'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  get #sectionId() {
    return this.dataset.sectionId;
  }

  get #messageAttributeName() {
    return this.dataset.messageAttribute || 'Gift Message';
  }

  get #currentLine() {
    return this.dataset.currentLine ? Number(this.dataset.currentLine) : null;
  }

  #showError() {
    this.refs.error.classList.remove('hidden');
  }

  #clearError() {
    this.refs.error.classList.add('hidden');
  }

  /**
   * Handles selecting a gift-wrap option (including "none").
   * @param {Event} event - The change event from a radio input.
   */
  selectOption = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || typeof this.#sectionId !== 'string') return;

    const variantId = target.value;
    const currentLine = this.#currentLine;
    const abortController = this.#createAbortController();

    this.#setBusy(true);
    this.#clearError();

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: variantId ? 'add' : 'remove',
        context: 'cart',
        lines: [{ id: variantId || this.dataset.currentVariant || '', quantity: variantId ? 1 : 0 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      /** @type {Record<string, string> | undefined} */
      let sections;

      if (currentLine) {
        const removeResponse = await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              line: currentLine,
              quantity: 0,
              sections: [this.#sectionId],
              sections_url: window.location.pathname,
            }),
          }),
          signal: abortController.signal,
        });
        const removeData = await removeResponse.json();
        sections = removeData.sections;
      }

      if (variantId) {
        const addResponse = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_WRAP_PROPERTY]: 'true' } }],
              sections: [this.#sectionId],
              sections_url: window.location.pathname,
            }),
          }),
          signal: abortController.signal,
        });
        const addData = await addResponse.json();

        if (addData.status) {
          throw new Error(addData.message || 'Failed to add gift wrap');
        }

        sections = addData.sections;
      }

      if (sections && sections[this.#sectionId]) {
        // /cart/add.js only returns the added line, not full cart totals, so
        // fetch the authoritative cart to get an accurate item count for
        // listeners like the header cart icon.
        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: abortController.signal,
        });
        const cartData = await cartResponse.json();

        deferredPromise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData) });
        morphSection(this.#sectionId, sections[this.#sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.#showError();
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to update gift wrap',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#setBusy(false);
      cartPerformance.measureFromEvent('gift-wrap-update:user-action', event);
    }
  };

  /**
   * Handles updates to the gift message. Stored as an order-level cart
   * attribute (not a line-item property), so it's independent of whether a
   * gift-wrap product is in the cart.
   * @param {InputEvent} event - The input event from the message textarea.
   */
  updateMessage = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement) || typeof this.#sectionId !== 'string') return;

    const abortController = this.#createAbortController();
    const message = event.target.value.trim();

    this.#clearError();

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            attributes: { [this.#messageAttributeName]: message },
            sections: [this.#sectionId],
            sections_url: window.location.pathname,
          }),
        }),
        signal: abortController.signal,
      });

      const data = await response.json();
      if (data.sections && data.sections[this.#sectionId]) {
        morphSection(this.#sectionId, data.sections[this.#sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        this.#showError();
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to update gift message',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  }, 300);

  /**
   * @param {boolean} isBusy
   */
  #setBusy(isBusy) {
    this.toggleAttribute('data-busy', isBusy);
    for (const input of this.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement) input.disabled = isBusy;
    }
  }
}

if (!customElements.get('cart-gift-wrap-component')) {
  customElements.define('cart-gift-wrap-component', CartGiftWrap);
}
